import { useParams } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

const categoryHeroImages: Record<string, string> = {
  all: 'https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20style%20illustration%20representing%20all%20genres%20with%20soft%20warm%20lighting%2C%20artistic%20composition%2C%20cozy%20reading%20vibe%2C%20muted%20colors%2C%20delicate%20artwork%2C%20various%20comic%20elements%20floating&width=1920&height=600&seq=category-hero-all&orientation=landscape',
  romance: 'https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20style%20romantic%20illustration%20with%20soft%20pink%20and%20warm%20tones%2C%20two%20characters%20in%20a%20tender%20moment%2C%20cherry%20blossom%20petals%2C%20dreamy%20atmosphere%2C%20romance%20genre&width=1920&height=600&seq=category-hero-romance&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20fantasy%20illustration%20with%20magical%20elements%2C%20mystical%20portal%2C%20glowing%20lights%2C%20epic%20atmosphere%2C%20dark%20blue%20and%20golden%20tones%2C%20fantasy%20genre&width=1920&height=600&seq=category-hero-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=A%20dynamic%20Korean%20manhwa%20action%20illustration%20with%20intense%20battle%20scene%2C%20dramatic%20lighting%2C%20sword%20clash%2C%20powerful%20energy%20effects%2C%20dark%20and%20fiery%20tones%2C%20action%20genre&width=1920&height=600&seq=category-hero-action&orientation=landscape',
  bl: 'https://readdy.ai/api/search-image?query=A%20beautiful%20soft%20Korean%20manhwa%20BL%20illustration%20with%20two%20handsome%20men%20in%20a%20tender%20moment%2C%20soft%20pastel%20colors%2C%20romantic%20atmosphere%2C%20flower%20petals%2C%20gentle%20lighting%2C%20BL%20genre&width=1920&height=600&seq=category-hero-bl&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=A%20colorful%20and%20funny%20Korean%20manhwa%20comedy%20illustration%20with%20exaggerated%20expressions%2C%20bright%20yellow%20and%20pink%20colors%2C%20school%20setting%2C%20energetic%20vibe%2C%20comedy%20genre&width=1920&height=600&seq=category-hero-comedy&orientation=landscape',
  thriller: 'https://readdy.ai/api/search-image?query=A%20mysterious%20dark%20Korean%20manhwa%20thriller%20illustration%20with%20noir%20atmosphere%2C%20streetlamp%20in%20alley%2C%20dramatic%20shadows%2C%20suspenseful%20mood%2C%20dark%20blue%20tones%2C%20thriller%20genre&width=1920&height=600&seq=category-hero-thriller&orientation=landscape',
  urban: 'https://readdy.ai/api/search-image?query=A%20modern%20Korean%20manhwa%20urban%20illustration%20with%20city%20skyline%2C%20office%20setting%2C%20warm%20lighting%2C%20contemporary%20vibe%2C%20clean%20aesthetic%2C%20urban%20genre&width=1920&height=600&seq=category-hero-urban&orientation=landscape',
  historical: 'https://readdy.ai/api/search-image?query=A%20elegant%20Korean%20manhwa%20historical%20illustration%20with%20ancient%20palace%2C%20traditional%20hanbok%2C%20cherry%20blossom%20trees%2C%20soft%20golden%20light%2C%20romantic%20historical%20atmosphere&width=1920&height=600&seq=category-hero-historical&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = slug === 'all'
    ? { name: '全部漫画', slug: 'all', description: '浏览非麻瓜漫画全部韩国漫画资源' }
    : categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto text-4xl text-foreground-300">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h2 className="mt-4 text-xl font-heading font-semibold text-foreground-900">分类未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">请返回首页浏览其他分类</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const filteredComics = slug === 'all'
    ? comics
    : comics.filter((c) => c.categorySlug === slug);

  const categoryTitle = slug === 'all'
    ? '全部韩国漫画 - 非麻瓜漫画'
    : `${category.name}韩国漫画 - 非麻瓜漫画`;

  const categoryDesc = slug === 'all'
    ? '浏览非麻瓜漫画全部韩国漫画资源，包含恋爱、奇幻、动作、耽美、搞笑、悬疑、都市、古风等全部类型，免费在线阅读。'
    : `免费在线观看${category.name}类韩国漫画，非麻瓜漫画提供海量${category.name}题材韩漫资源，支持下拉式阅读，每日更新。`;

  const categoryKeywords = slug === 'all'
    ? '全部韩漫,韩国漫画大全,免费漫画合集,下拉式漫画'
    : `${category.name}韩漫,${category.name}漫画,${category.name}韩国漫画免费,下拉式漫画`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: categoryTitle,
      description: categoryDesc,
      url: `http://wa25w.cn/category/${slug}`,
      inLanguage: 'zh-CN',
      isPartOf: {
        '@type': 'WebSite',
        name: '非麻瓜漫画',
        url: 'http://wa25w.cn/',
      },
    },
  ];

  const heroImage = categoryHeroImages[slug || 'all'] || categoryHeroImages.all;

  return (
    <>
      <PageMeta
        title={categoryTitle}
        description={categoryDesc}
        keywords={categoryKeywords}
        canonicalUrl={`http://wa25w.cn/category/${slug}`}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main className="pt-16">
        <section className="relative w-full min-h-[320px] md:min-h-[400px] flex items-center overflow-hidden">
          <img
            src={heroImage}
            alt={`${category.name}韩国漫画`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/50"></div>

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10">
            <Breadcrumb
              items={[
                { name: '首页', url: '/' },
                { name: category.name, url: `/category/${slug}` },
              ]}
            />
            <div className="mt-6 flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-3/5 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight">
                  {category.name}
                  {category.name.length <= 4 && (
                    <>
                      <span className="block mt-1 text-primary-400">韩国漫画</span>
                    </>
                  )}
                </h1>
                <p className="mt-4 text-sm md:text-base text-background-200/80 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                  {category.description || `浏览非麻瓜漫画${category.name}分类下的全部韩国漫画资源`}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                <select className="text-sm bg-background-100 border border-background-200 rounded-lg px-3 py-2 text-foreground-700 outline-none cursor-pointer">
                  <option value="latest">最新更新</option>
                  <option value="popular">最受欢迎</option>
                  <option value="rating">评分最高</option>
                </select>
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto text-4xl text-foreground-300">
                  <i className="ri-book-2-line"></i>
                </span>
                <p className="mt-4 text-foreground-500">该分类暂无漫画内容</p>
              </div>
            )}

            {filteredComics.length > 12 && (
              <div className="mt-10 flex justify-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium cursor-pointer">1</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 text-sm cursor-pointer">2</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 text-sm cursor-pointer">3</button>
                <span className="w-9 h-9 flex items-center justify-center text-foreground-400 text-sm">...</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 text-sm cursor-pointer">8</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}